/**
 * VoiceButton Component
 * Toggle button for voice conversation with state indicators.
 */

import type {
  VoiceButtonProps,
  ConversationState,
  ConversationMode,
} from "../../types/voice";

/** Get label based on current state and mode */
function getLabel(
  state: ConversationState,
  mode: ConversationMode,
  isActive: boolean,
): string {
  if (!isActive && state === "idle") {
    return mode.input === "voice" ? "Start Voice" : "Chat";
  }

  switch (state) {
    case "idle":
      return "Start Voice";
    case "listening":
      return "Listening...";
    case "processing":
      return "Processing...";
    case "thinking":
      return "Thinking...";
    case "speaking":
      return "Speaking...";
    default:
      return "Voice";
  }
}

/** State-based button styling - Dracula theme */
const STATE_CLASSES: Record<ConversationState, string> = {
  idle: "bg-primary-500 hover:bg-primary-600",
  listening: "bg-accent-green hover:bg-accent-green/80",
  processing: "bg-accent-orange",
  thinking: "bg-accent-yellow text-dark-bg",
  speaking: "bg-accent-pink",
};

export function VoiceButton({
  state,
  isActive,
  mode,
  onClick,
  className = "",
}: VoiceButtonProps) {
  const label = getLabel(state, mode, isActive);
  const stateClass = STATE_CLASSES[state];
  const showStop = isActive && state !== "idle";

  return (
    <button
      onClick={onClick}
      className={`
        px-4 py-2 rounded-lg text-white font-medium text-sm
        transition-all duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-bg focus:ring-primary-500
        disabled:opacity-50 disabled:cursor-not-allowed
        flex items-center gap-2
        ${stateClass}
        ${className}
      `}
      aria-label={showStop ? "Stop voice" : label}
    >
      {/* Microphone icon */}
      {state === "idle" && !isActive && (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
      )}

      {/* Recording indicator */}
      {state === "listening" && (
        <span className="w-2.5 h-2.5 bg-accent-red rounded-full animate-pulse" />
      )}

      {/* Spinner for processing/thinking states */}
      {(state === "processing" || state === "thinking") && (
        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}

      {/* Audio playing indicator */}
      {state === "speaking" && (
        <span className="flex gap-0.5 items-end h-4">
          <span
            className="w-1 bg-white rounded animate-bounce"
            style={{ height: "8px", animationDelay: "0ms" }}
          />
          <span
            className="w-1 bg-white rounded animate-bounce"
            style={{ height: "12px", animationDelay: "150ms" }}
          />
          <span
            className="w-1 bg-white rounded animate-bounce"
            style={{ height: "8px", animationDelay: "300ms" }}
          />
        </span>
      )}

      {/* Stop icon when active */}
      {showStop && (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <rect x="6" y="6" width="12" height="12" rx="1" />
        </svg>
      )}

      <span>{showStop ? "Stop" : label}</span>
    </button>
  );
}
